import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, tap } from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { v4 as uuid } from 'uuid';
import { CryptoStorageService } from '@services/storage-service.service';
import { HttpResponseMultiWarning } from './interfaces/conversion-go.interface';

@Injectable()
export class ParamInterceptor implements HttpInterceptor {
  constructor(
    private msg: NzNotificationService,
    private router: Router,
    private cryptoStorageService: CryptoStorageService
  ) { }

  businessApiPaths = [
    '/api/v1/communications/BUSINESS',
    '/v1/dbp/business-signature/initial-verifications',
    '/v1/dbp/business-signature/otp',
    '/v1/dbp/business-signature/document-verifications',
  ];

  private addAuthHeaders(request: HttpRequest<any>): HttpRequest<any> {
    const userData = this.cryptoStorageService.getFromSessionStorage('T');
    const token = userData ? userData.access_token : '';
    const uuidExist = this.getDeviceId();

    const headers = request.headers
      .set('Authorization', `Bearer ${token}`)
      .set('X-Request-ID', uuid())
      .set('Accept-Language', this.getAcceptLanguage({ isFormattedLang: true }) || 'ru-RU')
      .set('UUID', uuidExist)
      .set('DEVICE_ID', uuidExist);

    return request.clone({
      headers
    });
  }

  getDeviceId = (): string => {
    let deviceId = localStorage.getItem('uuid');
    if (!deviceId) {
      deviceId = uuid();
      localStorage.setItem('uuid', deviceId);
    }
    return deviceId;
  }

  getAcceptLanguage = ({ isFormattedLang }: { isFormattedLang: boolean }): string | null => {
    const lang = localStorage.getItem('lang');
    if (isFormattedLang) {
      switch (lang) {
        case 'ru':
          return 'ru-RU';
        case 'en':
          return 'en-US';
        case 'kz':
          return 'kk-KZ';
        case 'zh':
          return 'zh-CN';
        default:
          return lang;
      }
    }
    return lang;
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (
      request.url.startsWith('/auth/realms/bank/protocol/openid-connect/token/') ||
      request.url.startsWith('/auth/realms/bank/devices') ||
      request.url.startsWith('/v1/dbp/business-frequently-asked-questions/')
    ) {
      return next.handle(request).pipe(
        catchError(error => {
          return throwError(error);
        })
      );
    } else if (
      request.url.startsWith('/v1/dbp/users/user-organizations')
    ) {
      const modifiedReq = this.addAuthHeaders(request);
      return next.handle(modifiedReq).pipe(
        catchError(error => {
          return throwError(error);
        })
      );
    } else if (this.businessApiPaths.some(path => request.url.startsWith(path))) {
      const modifiedReq = this.addAuthHeaders(request);
      return next.handle(modifiedReq);
    } else {
      const modifiedReq = this.addAuthHeaders(request);

      return next.handle(modifiedReq).pipe(
        tap((event: HttpEvent<any>): void => {
          if (event instanceof HttpResponse && event.status === 207) {
            const responseBody = event.body;
            if (responseBody?.errList && Array.isArray(responseBody.errList)) {
              responseBody.errList.forEach((warning: HttpResponseMultiWarning) =>
                this.msg.warning('Предупреждение', warning.reason)
              );
            } else {
              this.msg.warning('Предупреждение', responseBody?.errMessage || 'Неизвестная ошибка');
            }
          }
        }),
        catchError(error => {
          if (error.status === 401) {
            this.handlePreventLogoutUrl(request.url, () => this.errorAuth());
            return throwError(error);
          } else if (error.status === 400 || error.status === 500) {
            return throwError(error);
          } else if (
            error.status === 400 &&
            request.url.startsWith('/v1/dbp/foreign-contracts/unvd/list')
            || error.status === 400
            && request.url.startsWith('/v1/dbp/foreign-contracts/rates/conversions'
            )
          ) {
            return throwError(error);
          } else if (
            error.status === 400
            && request.url.startsWith('/v1/dbp/business/deposits/report/individual/list')) {
            return throwError(error);
          } else if (this.isBusinessError(error)) {
            this.msg.error('Ошибка', this.getBusinessErrorMessage(error) || this.getDefaultErrorMessage());
          } else if (this.isTechnicalError(error)) {
            this.msg.error('Ошибка', 'Сервис временно недоступен, попробуйте снова позже.');
          } else if (this.isHtmlError(error)) {
            this.msg.error('Ошибка', 'Ресурс не найден или произошла ошибка. Попробуйте снова позже.');
          } else {
            console.error('Техническая ошибка:', error);
            this.msg.error('Ошибка', this.getDefaultErrorMessage());
          }
          return throwError(error);
        })
      );
    }
  }

  handlePreventLogoutUrl(currentUrl: string, next: () => void): void {
    const preventLogoutUrls = ['/v1/dbp/salary/directory/projectcode'];
    for (const url of preventLogoutUrls) {
      if (currentUrl.includes(url)) {
        return;
      }
    }
    next();
  }

  private isBusinessError(error: any): boolean {
    return (
      error?.error?.message ||
      error?.error?.devMessage ||
      error?.error?.errMessage ||
      (error?.error?.detailsList && error.error.detailsList.length > 0)
    );
  }

  private getBusinessErrorMessage(error: any): string | null {
    if (error.error.message) {
      return error.error.message;
    } else if (error.error.errMessage) {
      return error.error.errMessage;
    } else if (error.error.detailsList) {
      return error.error.detailsList.map((el: any) => el.text).join('<br/>');
    }
    return null;
  }

  private isTechnicalError(error: any): boolean {
    const technicalStatusCodes = [500, 502, 503, 504, 507, 509];
    return technicalStatusCodes.includes(error.status);
  }

  private isHtmlError(error: any): boolean {
    const contentType = error.headers?.get('Content-Type');
    return contentType && contentType.includes('text/html');
  }

  private getDefaultErrorMessage(): string {
    return 'Произошла непредвиденная ошибка. Пожалуйста, попробуйте позже.';
  }

  private errorAuth(): void {
    this.msg.error('Ошибка авторизации', 'Ваша сессия истекла');
    sessionStorage.clear();
    localStorage.removeItem('AUTH_METHOD');
    this.router.navigate(['/']);
  }
}
